import React, { forwardRef, useContext } from 'react';
import { useController } from 'react-hook-form';
import { useMergeRefs } from '../../hooks/useMergeRefs';
import { DatePickerProps } from '../DatePicker';
import { DatePickerField } from '../DatePicker/DatePickerField';
import { FormContext } from './Form';
import { BoundComponentProps } from './types';

export type BoundDatePickerProps = DatePickerProps & BoundComponentProps;

export const BoundDatePicker = forwardRef<
  HTMLButtonElement,
  BoundDatePickerProps
>(
  (
    {
      id,
      control,
      name,
      defaultValue,
      rules,
      label,
      required,
      readOnly,
      disabled,
      onChange: outerOnChange,
      ...rest
    },
    ref,
  ) => {
    const { control: contextControl } = useContext(FormContext);

    const {
      field: { ref: internalRef, value, onChange, ...datePickerProps },
      fieldState: { error },
    } = useController({
      name,
      control: control ?? contextControl,
      defaultValue,
      rules: {
        required: required && 'This field is required',
        ...rules,
      },
    });

    const combinedRef = useMergeRefs(ref, internalRef);

    return (
      <DatePickerField
        ref={combinedRef as any}
        id={id}
        label={label}
        value={value}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        error={error?.message}
        onChange={(date: Date | undefined) => {
          onChange(date);
          outerOnChange?.(date);
        }}
        {...datePickerProps}
        {...rest}
      />
    );
  },
);

BoundDatePicker.displayName = 'BoundDatePicker';
